<template>
  <section class="tm-root">
    <div class="tm-toolbar">
      <div class="tm-title-wrap">
        <h2 class="tm-title">Equipes</h2>
        <span class="tm-count">{{ teams.length }} equipe{{ teams.length !== 1 ? 's' : '' }}</span>
      </div>
      <div v-if="accessMode === 'edit'" class="tm-toolbar-actions">
        <button class="tm-btn tm-btn-ghost tm-btn-sm" type="button" @click="showCreateForm = !showCreateForm">
          {{ showCreateForm ? 'Fechar' : '+ Equipe' }}
        </button>
      </div>
    </div>

    <Transition name="slide-down">
      <div v-if="showCreateForm && accessMode === 'edit'" class="tm-panel">
        <div class="tm-panel-inner">
          <div class="tm-panel-header">
            <span class="tm-panel-title">Nova equipe</span>
            <button class="tm-close-btn" type="button" @click="showCreateForm = false">✕</button>
          </div>
          <form class="tm-form" @submit.prevent="submitCreateTeam">
            <div class="tm-form-row">
              <div class="tm-field">
                <label>Nome da equipe</label>
                <input v-model="newTeam.name" type="text" placeholder="Ex: Montagem Estrutural" />
              </div>
              <label class="tm-switch">
                <input v-model="newTeam.isGlobal" type="checkbox" />
                <span>Equipe global</span>
              </label>
            </div>

            <div class="tm-field">
              <label>Adicionar pessoas</label>
              <input
                v-model="userSearch"
                type="text"
                placeholder="Buscar por nome ou usuário"
                class="tm-search"
              />
              <div v-if="filteredAvailableUsers.length > 0" class="tm-user-list">
                <button
                  v-for="user in filteredAvailableUsers"
                  :key="user.username"
                  class="tm-user-option"
                  type="button"
                  @click="addUserToNewTeam(user.username)"
                >
                  <span class="tm-user-name">{{ user.name }}</span>
                  <span class="tm-user-meta">@{{ user.username }}</span>
                </button>
              </div>
            </div>

            <div v-if="newTeam.members.length > 0" class="tm-members-block">
              <span class="tm-block-label">Membros selecionados</span>
              <div class="tm-member-tags">
                <span v-for="member in newTeam.members" :key="member" class="tm-member-tag">
                  {{ displayUser(member) }}
                  <button type="button" class="tm-tag-remove" @click.prevent="removeUserFromNewTeam(member)">✕</button>
                </span>
              </div>
            </div>

            <div class="tm-panel-footer">
              <button class="tm-btn tm-btn-ghost tm-btn-sm" type="button" @click="showCreateForm = false">Cancelar</button>
              <button class="tm-btn tm-btn-primary tm-btn-sm" type="submit">Criar equipe</button>
            </div>
            <p v-if="formError" class="tm-error">{{ formError }}</p>
          </form>
        </div>
      </div>
    </Transition>

    <div v-if="teams.length === 0" class="tm-empty">
      <div class="tm-empty-icon">👥</div>
      <p>Nenhuma equipe criada.</p>
      <button v-if="accessMode === 'edit'" class="tm-btn tm-btn-primary tm-btn-sm" type="button" @click="showCreateForm = true">
        Criar equipe
      </button>
    </div>

    <div v-else class="tm-grid">
      <article v-for="team in teams" :key="team.id" class="tm-card">
        <div class="tm-card-header">
          <div class="tm-card-title-wrap">
            <div class="tm-card-icon">
              <svg width="28" height="22" viewBox="0 0 38 30" fill="none">
                <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.25"/>
                <rect x="0" y="3" width="16" height="6" rx="2" fill="currentColor" opacity="0.4"/>
                <rect x="0" y="6" width="38" height="24" rx="3" fill="currentColor" opacity="0.15"/>
              </svg>
            </div>
            <div>
              <h3 class="tm-card-title">{{ team.name }}</h3>
              <div class="tm-card-sub">{{ team.members.length }} membro{{ team.members.length !== 1 ? 's' : '' }}</div>
            </div>
          </div>
          <div class="tm-card-top-actions">
            <span class="tm-badge" :class="team.isGlobal ? 'tm-badge-global' : 'tm-badge-local'">
              {{ team.isGlobal ? 'Global' : 'Local' }}
            </span>
            <div v-if="accessMode === 'edit'" class="tm-card-hover">
              <button class="tm-action-btn" title="Editar" type="button" @click="openEditTeam(team)">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5a1.5 1.5 0 012 2L5 13H3v-2L11.5 2.5z" stroke="currentColor" stroke-width="1.3" fill="none"/></svg>
              </button>
              <button class="tm-action-btn tm-action-danger" title="Excluir" type="button" @click="deleteTeam(team.id)">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6 4V2h4v2M5 4v9a1 1 0 001 1h4a1 1 0 001-1V4H5z" stroke="currentColor" stroke-width="1.3" fill="none"/></svg>
              </button>
            </div>
          </div>
        </div>

        <div v-if="team.members.length === 0" class="tm-empty-members">Sem membros</div>
        <div v-else class="tm-member-cloud">
          <span v-for="member in team.members" :key="member" class="tm-member-pill">
            {{ displayUser(member) }}
          </span>
        </div>
      </article>
    </div>

    <Teleport to="body">
      <Transition name="viewer-fade">
        <div v-if="editingTeam" class="tm-overlay" @click.self="editingTeam = null">
          <div class="tm-modal">
            <div class="tm-panel-header">
              <span class="tm-panel-title">Editar equipe</span>
              <button class="tm-close-btn" type="button" @click="editingTeam = null">✕</button>
            </div>
            <form class="tm-form" @submit.prevent="submitEditTeam">
              <div class="tm-form-row">
                <div class="tm-field">
                  <label>Nome da equipe</label>
                  <input v-model="editingTeam.name" type="text" />
                </div>
                <label class="tm-switch">
                  <input v-model="editingTeam.isGlobal" type="checkbox" />
                  <span>Equipe global</span>
                </label>
              </div>

              <div class="tm-field">
                <label>Adicionar pessoas</label>
                <input
                  v-model="editUserSearch"
                  type="text"
                  placeholder="Buscar por nome ou usuário"
                  class="tm-search"
                />
                <div v-if="filteredEditUsers.length > 0" class="tm-user-list">
                  <button
                    v-for="user in filteredEditUsers"
                    :key="user.username"
                    class="tm-user-option"
                    type="button"
                    @click="addUserToEditTeam(user.username)"
                  >
                    <span class="tm-user-name">{{ user.name }}</span>
                    <span class="tm-user-meta">@{{ user.username }}</span>
                  </button>
                </div>
              </div>

              <div v-if="editingTeam.members.length > 0" class="tm-members-block">
                <span class="tm-block-label">Membros da equipe</span>
                <div class="tm-member-tags">
                  <span v-for="member in editingTeam.members" :key="member" class="tm-member-tag">
                    {{ displayUser(member) }}
                    <button type="button" class="tm-tag-remove" @click.prevent="removeUserFromEditTeam(member)">✕</button>
                  </span>
                </div>
              </div>

              <div class="tm-panel-footer">
                <button class="tm-btn tm-btn-ghost tm-btn-sm" type="button" @click="editingTeam = null">Cancelar</button>
                <button class="tm-btn tm-btn-primary tm-btn-sm" type="submit">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import { apiFetch } from '@/store/api'
import { uiStore } from '@/store/ui'

interface Team {
  id: string
  name: string
  members: string[]
  isGlobal: boolean
}

interface AssignableUser {
  username: string
  name: string
}

export default defineComponent({
  name: 'ProjectTeamsSection',
  props: {
    projectId: {
      type: String,
      required: true
    },
    accessMode: {
      type: String as () => 'read' | 'edit',
      default: 'read'
    },
    allUsers: {
      type: Array as () => AssignableUser[],
      default: () => []
    }
  },
  setup(props) {
    const teams = ref<Team[]>([])
    const showCreateForm = ref(false)
    const formError = ref('')
    const userSearch = ref('')
    const editUserSearch = ref('')
    const editingTeam = ref<Team | null>(null)

    const newTeam = ref({
      name: '',
      members: [] as string[],
      isGlobal: false
    })

    const filteredAvailableUsers = ref<AssignableUser[]>([])
    const filteredEditUsers = ref<AssignableUser[]>([])

    const displayUser = (username: string) =>
      props.allUsers.find((user) => user.username === username)?.name || username

    const loadTeams = async () => {
      if (!props.projectId) return
      try {
        teams.value = await apiFetch<Team[]>(`/projects/${encodeURIComponent(props.projectId)}/teams`)
      } catch (_err) {
        teams.value = []
      }
    }

    const updateFilteredUsers = () => {
      const q = userSearch.value.toLowerCase().trim()
      filteredAvailableUsers.value = props.allUsers.filter((user) => {
        if (newTeam.value.members.includes(user.username)) return false
        const haystack = `${user.name} ${user.username}`.toLowerCase()
        if (q && !haystack.includes(q)) return false
        return true
      })
    }

    const updateFilteredEditUsers = () => {
      if (!editingTeam.value) return
      const q = editUserSearch.value.toLowerCase().trim()
      filteredEditUsers.value = props.allUsers.filter((user) => {
        if (editingTeam.value?.members.includes(user.username)) return false
        const haystack = `${user.name} ${user.username}`.toLowerCase()
        if (q && !haystack.includes(q)) return false
        return true
      })
    }

    const addUserToNewTeam = (username: string) => {
      if (!newTeam.value.members.includes(username)) {
        newTeam.value.members.push(username)
      }
      userSearch.value = ''
      updateFilteredUsers()
    }

    const removeUserFromNewTeam = (username: string) => {
      newTeam.value.members = newTeam.value.members.filter((member) => member !== username)
      updateFilteredUsers()
    }

    const addUserToEditTeam = (username: string) => {
      if (editingTeam.value && !editingTeam.value.members.includes(username)) {
        editingTeam.value.members.push(username)
      }
      editUserSearch.value = ''
      updateFilteredEditUsers()
    }

    const removeUserFromEditTeam = (username: string) => {
      if (editingTeam.value) {
        editingTeam.value.members = editingTeam.value.members.filter((member) => member !== username)
      }
      updateFilteredEditUsers()
    }

    const submitCreateTeam = async () => {
      formError.value = ''
      if (!newTeam.value.name.trim()) {
        formError.value = 'Nome da equipe é obrigatório.'
        return
      }
      if (newTeam.value.members.length === 0) {
        formError.value = 'Adicione pelo menos um membro.'
        return
      }
      try {
        await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/teams`, {
          method: 'POST',
          body: JSON.stringify({
            name: newTeam.value.name,
            members: newTeam.value.members,
            isGlobal: newTeam.value.isGlobal
          })
        })
        showCreateForm.value = false
        newTeam.value = { name: '', members: [], isGlobal: false }
        userSearch.value = ''
        await loadTeams()
      } catch (err) {
        formError.value = `Erro ao criar equipe: ${err}`
      }
    }

    const submitEditTeam = async () => {
      if (!editingTeam.value) return
      try {
        await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/teams/${encodeURIComponent(editingTeam.value.id)}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: editingTeam.value.name,
            members: editingTeam.value.members,
            isGlobal: editingTeam.value.isGlobal
          })
        })
        editingTeam.value = null
        await loadTeams()
      } catch (err) {
        console.error('Erro ao atualizar equipe:', err)
      }
    }

    const openEditTeam = (team: Team) => {
      editingTeam.value = { ...team, members: [...team.members] }
      editUserSearch.value = ''
      updateFilteredEditUsers()
    }

    const deleteTeam = async (teamId: string) => {
      if (!await uiStore.confirm('Tem certeza que deseja deletar esta equipe?')) return
      try {
        await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/teams/${encodeURIComponent(teamId)}`, {
          method: 'DELETE'
        })
        await loadTeams()
      } catch (err) {
        console.error('Erro ao deletar equipe:', err)
      }
    }

    watch(() => newTeam.value.members, () => {
      updateFilteredUsers()
    })

    watch(() => editingTeam.value?.members, () => {
      updateFilteredEditUsers()
    })

    watch(() => userSearch.value, () => {
      updateFilteredUsers()
    })

    watch(() => editUserSearch.value, () => {
      updateFilteredEditUsers()
    })

    watch(() => props.projectId, () => {
      loadTeams()
    })

    loadTeams()

    return {
      teams,
      showCreateForm,
      formError,
      userSearch,
      editUserSearch,
      editingTeam,
      newTeam,
      filteredAvailableUsers,
      filteredEditUsers,
      displayUser,
      addUserToNewTeam,
      removeUserFromNewTeam,
      addUserToEditTeam,
      removeUserFromEditTeam,
      submitCreateTeam,
      submitEditTeam,
      openEditTeam,
      deleteTeam
    }
  }
})
</script>

<style scoped lang="scss">
.tm-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 10px 12px;
}

.tm-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.tm-title-wrap {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.tm-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #2c2b27;
}

.tm-count {
  font-size: 13px;
  color: #7c6753;
  font-weight: 600;
}

.tm-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 700;
}

.tm-btn-sm {
  padding: 9px 14px;
  font-size: 13px;
}

.tm-btn-primary {
  background: #2f6fce;
  color: #fff;
}

.tm-btn-ghost {
  background: rgba(31, 30, 26, 0.06);
  color: #2c2b27;
}

.tm-panel {
  border-radius: 18px;
  background: linear-gradient(180deg, #fffdf8 0%, #f7f1e8 100%);
  border: 1px solid rgba(31, 30, 26, 0.08);
  box-shadow: 0 16px 36px rgba(71, 53, 27, 0.06);
}

.tm-panel-inner,
.tm-modal {
  padding: 18px;
}

.tm-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.tm-panel-title {
  font-size: 16px;
  font-weight: 800;
  color: #2c2b27;
}

.tm-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 999px;
  background: rgba(31, 30, 26, 0.06);
  cursor: pointer;
}

.tm-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tm-form-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: end;
}

.tm-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tm-field label,
.tm-block-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7c6753;
}

.tm-field input[type='text'],
.tm-search {
  width: 100%;
  border: 1px solid rgba(31, 30, 26, 0.14);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  background: #fff;
}

.tm-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 12px;
  border-radius: 10px;
  background: rgba(15, 118, 110, 0.08);
  color: #0f766e;
  font-size: 13px;
  font-weight: 700;
}

.tm-user-list {
  max-height: 220px;
  overflow: auto;
  border: 1px solid rgba(31, 30, 26, 0.08);
  border-radius: 14px;
  background: #fff;
}

.tm-user-option {
  width: 100%;
  border: none;
  border-bottom: 1px solid rgba(31, 30, 26, 0.06);
  background: transparent;
  padding: 11px 12px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.tm-user-option:last-child {
  border-bottom: none;
}

.tm-user-option:hover {
  background: rgba(47, 111, 206, 0.05);
}

.tm-user-name {
  font-size: 14px;
  font-weight: 700;
  color: #2c2b27;
}

.tm-user-meta {
  font-size: 12px;
  color: #7c6753;
}

.tm-members-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tm-member-tags,
.tm-member-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tm-member-tag,
.tm-member-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(47, 111, 206, 0.08);
  color: #2f6fce;
  font-size: 13px;
  font-weight: 700;
}

.tm-tag-remove {
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
}

.tm-panel-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.tm-error {
  margin: 0;
  color: #b3472a;
  font-size: 13px;
  font-weight: 600;
}

.tm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 280px;
  border: 1px dashed rgba(31, 30, 26, 0.14);
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(255, 251, 243, 0.9) 0%, rgba(247, 240, 229, 0.95) 100%);
  text-align: center;
  color: #7c6753;
}

.tm-empty-icon {
  font-size: 32px;
}

.tm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}

.tm-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(31, 30, 26, 0.1);
  background: linear-gradient(180deg, #fffdf8 0%, #f8f4ed 100%);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.tm-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 36px rgba(71, 53, 27, 0.08);
  border-color: rgba(47, 111, 206, 0.18);
}

.tm-card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.tm-card-title-wrap {
  display: flex;
  gap: 12px;
  min-width: 0;
}

.tm-card-icon {
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 14px;
  background: linear-gradient(180deg, #fbf5e8 0%, #f0e2bf 100%);
  display: grid;
  place-items: center;
  color: #9b7b47;
}

.tm-card-title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 800;
  color: #2c2b27;
}

.tm-card-sub {
  font-size: 12px;
  color: #7c6753;
}

.tm-card-top-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.tm-badge {
  padding: 7px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.tm-badge-global {
  background: rgba(15, 118, 110, 0.1);
  color: #0f766e;
}

.tm-badge-local {
  background: rgba(124, 103, 83, 0.12);
  color: #7c6753;
}

.tm-card-hover {
  display: flex;
  gap: 8px;
}

.tm-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  color: #4a4036;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(53, 43, 30, 0.12);
}

.tm-action-danger {
  color: #c2410c;
}

.tm-empty-members {
  font-size: 13px;
  color: #7c6753;
}

.tm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.7);
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 20px;
}

.tm-modal {
  width: min(720px, 100%);
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.18s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.viewer-fade-enter-active,
.viewer-fade-leave-active {
  transition: opacity 0.18s ease;
}

.viewer-fade-enter-from,
.viewer-fade-leave-to {
  opacity: 0;
}

@media (max-width: 760px) {
  .tm-root {
    padding: 0 4px 10px;
  }

  .tm-form-row {
    grid-template-columns: 1fr;
  }

  .tm-grid {
    grid-template-columns: 1fr;
  }

  .tm-card-header {
    flex-direction: column;
  }

  .tm-card-top-actions {
    align-items: flex-start;
  }
}
</style>
